import { Link } from 'react-router-dom';
import MarketingLayout from '../../components/MarketingLayout';
import { appLinks } from '../../lib/crossSiteLinks';
import { tokens } from '../../components/SEOPageLayout';
import { BreadcrumbJsonLd, FaqJsonLd } from '../../components/StructuredData';
import { SeoLongformSections } from '../../components/SeoLongformSections';

export default function Iso27001GitHubCompliancePage() {
  const faqs = [
    {
      question: 'What GitHub settings do ISO 27001 A.8.32 auditors check?',
      answer:
        'ISO 27001 A.8.32 auditors focus on whether your change management procedure is documented, enforced, and evidenced throughout the year. In GitHub they check branch protection rules (who can merge, required approvals, required CI checks), evidence that protection was active at the time of each change, PR approval records with reviewer identities, CI check results, and exception records for any emergency changes that bypassed normal controls.',
    },
    {
      question: 'How is ISO 27001 evidence different from SOC 2 evidence in GitHub?',
      answer:
        'The underlying GitHub evidence is largely the same — PR approvals, branch protection snapshots, CI check results, exception records. The key difference is scope and duration. ISO 27001 requires continuous evidence across the full certificate period (3 years with annual surveillance audits), whereas SOC 2 Type II covers a defined 6–12 month audit period. ISO 27001 also requires broader ISMS documentation — risk assessment, management review, internal audit — that SOC 2 does not explicitly require.',
    },
    {
      question: 'What is the ISO 27001 certificate period and how does it affect evidence collection?',
      answer:
        'ISO 27001 certificates are valid for 3 years. Surveillance audits occur in year 1 and year 2, and a full recertification audit happens at the end of year 3. Each surveillance audit samples evidence from the period since the last audit — roughly 12 months. This means evidence collection must be continuous throughout the certificate period, not just in preparation for audits.',
    },
    {
      question: 'Does ISO 27001 A.8.32 require branch protection on all branches?',
      answer:
        'A.8.32 requires a formal change management procedure for changes to "information processing facilities and systems." In practice, auditors focus on branches that gate production deployments — typically main, master, or release branches. Development branches may also be in scope if merges to them trigger automated deployments. The key question is whether every production-bound change goes through an authorized, reviewed, tested process.',
    },
    {
      question: 'How does A.8.32 relate to A.8.3, A.8.5, and other Annex A access controls?',
      answer:
        'A.8.32 (Change Management) covers the change process itself. A.8.3 (Information Access Restriction) and A.8.5 (Secure Authentication) cover who can access systems. In GitHub, branch protection rules sit at the intersection — they enforce both that only authorized individuals can approve changes (access control) and that changes follow the formal process (change management). Evidence for both controls comes from the same GitHub configuration and event records.',
    },
    {
      question: 'Can EvidentTrail evidence satisfy both ISO 27001:2022 and ISO 27001:2013?',
      answer:
        'Yes. A.8.32 in the 2022 version corresponds to A.12.1.2 in the 2013 version. The evidence requirements are substantially equivalent — both require authorized, tested, and documented changes. EvidentTrail evidence packs are labelled with the 2022 control numbers but map directly to the equivalent 2013 controls.',
    },
  ];

  const longformSections = [
    {
      title: 'What ISO 27001 A.8.32 requires from your GitHub configuration',
      paragraphs: [
        'ISO 27001 Annex A control A.8.32 requires a formal change management procedure that covers authorization, risk assessment, testing, approval, documentation, and exception handling for every change to information processing facilities. For software teams, this procedure lives in GitHub — the pull request workflow, branch protection rules, and CI pipeline are the technical implementation of the required formal process.',
        'Unlike SOC 2, which is an audit of a defined period, ISO 27001 is an ongoing management system commitment. The surveillance auditor who visits in year 1 of your certificate will sample changes from the months since your initial certification. The evidence must show that the process operated consistently — not that you prepared for the audit.',
        'The practical implication is that GitHub configuration must be enforced continuously, not configured in advance of each audit. Branch protection rules must remain active. Required CI checks must remain required. Any gap — a period where branch protection was disabled, a series of merges without required reviews — creates a finding that may require a corrective action plan.',
      ],
      bullets: [
        'Authorization: every change must be formally authorized before implementation — pull request creation and required reviewer approval',
        'Testing: changes must be tested before production deployment — required CI status checks capturing pass/fail with timestamps',
        'Approval: an authorized individual must approve each change — reviewer identity and approval timestamp per PR',
        'Exception handling: emergency changes outside normal procedure must be documented — exception records with lifecycle tracking',
        'Continuous: evidence must cover the full certificate period — not pre-audit snapshots',
      ],
    },
    {
      title: 'GitHub configuration checklist for ISO 27001 A.8.32',
      paragraphs: [
        'The following GitHub settings are the most commonly reviewed in ISO 27001 A.8.32 assessments. These settings are the technical controls. The evidence that these settings were active throughout the audit period is what EvidentTrail provides — configuration snapshots at each change event, continuous PR approval records, and drift detection for any periods where settings changed.',
        'Branch protection on all production-gating branches should require pull request reviews before merging (minimum one approver, dismiss stale reviews on new commits), require status checks to pass before merging, include administrators in the protection rule so no account can bypass it, restrict who can push directly to the branch, and disallow force pushes to prevent history rewriting.',
        'At the organization level, two-factor authentication should be required for all members. Secret scanning should be enabled to detect exposed credentials. Repository access should follow least privilege principles and be reviewed at least annually — access review records satisfy the A.8.3 access restriction requirement alongside the A.8.32 change management evidence.',
      ],
      bullets: [
        'Require PR reviews: minimum 1 approver, dismiss stale reviews, include administrators',
        'Require status checks: CI pipeline must pass — linked test and security scan results',
        'Restrict push access: only authorized accounts or automation can push directly',
        'Disallow force pushes: prevents history rewriting on protected branches',
        'Require 2FA: enforced at GitHub organization level for all members',
        'Secret scanning: alerts for exposed credentials satisfy monitoring requirements',
        'Access review records: quarterly or annual review of repository permissions',
      ],
    },
    {
      title: 'Branch protection drift: the A.8.32 failure mode most teams miss',
      paragraphs: [
        'The most common A.8.32 audit finding for GitHub-based teams is not missing approvals — it is unrecorded periods where branch protection was weakened or disabled. A developer encounters a build pipeline issue and temporarily disables a required status check. A branch protection rule is adjusted to allow a deadline deployment and never reverted. These events are invisible in GitHub\'s current settings view, which shows only the current state.',
        'ISO 27001 surveillance auditors sample changes from across the period. If they find a merge event that occurred during a window when branch protection was disabled, they will ask for the exception documentation. If there is none, it becomes a finding. If the pattern is repeated, it may become a major nonconformity.',
        'EvidentTrail drift detection captures every change to branch protection rules as a timestamped event with before and after configuration snapshots. When combined with the PR evidence records, the auditor can see exactly what protection was in place at the time of each sampled change — including any periods of reduced protection and whether those periods were covered by documented exceptions.',
      ],
      bullets: [
        'Drift events: every branch protection change captured with before/after snapshots and timestamp',
        'Severity classification: distinguishes between configuration tuning and protection removal',
        'Gap detection: merges during reduced-protection windows are flagged in evidence packs',
        'Exception linkage: emergency bypass events linked to exception records for A.8.32 documentation',
      ],
    },
    {
      title: 'ISO 27001 evidence period vs SOC 2: key differences',
      paragraphs: [
        'SOC 2 Type II defines a specific audit period — typically 6 or 12 months — and the auditor reviews evidence from that period only. ISO 27001 surveillance audits sample from the period since the last audit, which is roughly 12 months but tied to when the previous audit occurred rather than a defined calendar window.',
        'The three-year certificate cycle means that the first full recertification audit at year 3 may review evidence patterns over a much longer horizon to assess whether the management system has matured. Auditors look for improvement, not just compliance — the year-over-year evidence records should show that drift events are decreasing and exception processes are improving.',
        'For teams pursuing both ISO 27001 and SOC 2, a single EvidentTrail installation covers both evidence requirements. The same GitHub event records satisfy CC8.1 (SOC 2) and A.8.32 (ISO 27001). The same branch protection snapshots satisfy CC6.1 (SOC 2) and A.8.3/A.8.5 (ISO 27001). The evidence pack can be scoped to the relevant period for each audit.',
      ],
      bullets: [
        'SOC 2 Type II: defined 6–12 month period, CPA firm audit',
        'ISO 27001: ongoing certificate, annual surveillance, 3-year recertification',
        'Shared evidence: 60–70% of GitHub evidence satisfies both frameworks simultaneously',
        'Single installation: EvidentTrail covers both — scope the evidence pack per audit',
      ],
    },
    {
      title: 'Common ISO 27001 A.8.32 audit findings and how to avoid them',
      paragraphs: [
        'The most frequently cited A.8.32 findings in software development audits fall into three categories: evidence gaps for sampled changes (the auditor asks for a PR from six months ago and the team cannot produce the branch protection configuration that was active at that time), undocumented exceptions (emergency merges with no linked justification or remediation record), and configuration drift without corrective action.',
        'Evidence gaps are addressed by continuous capture — EvidentTrail stores timestamped snapshots of every configuration at the time of each change event. There is no retrospective reconstruction; the evidence was captured when the event occurred. For a surveillance audit 11 months after initial certification, every change from the first day of the certificate period is available for auditor sampling.',
        'Undocumented exceptions are addressed by exception tracking — every emergency merge that bypasses branch protection is captured as an exception record. The record includes the bypass event, the PR it relates to, a field for justification, and lifecycle tracking for when the exception was acknowledged and what remediation was taken. This turns an audit finding into documented evidence of a controlled process.',
      ],
      bullets: [
        'Evidence gap: configuration history unavailable for sampled changes — EvidentTrail captures snapshots at event time',
        'Undocumented exception: emergency merge with no justification record — exception lifecycle tracking covers this',
        'Repeated drift: same protection rule disabled multiple times — drift frequency visible in evidence pack',
        'Self-merge: author and approver are the same person — EvidentTrail flags segregation of duty violations',
      ],
    },
  ];

  return (
    <MarketingLayout
      title="ISO 27001 GitHub Compliance: A.8.32 Evidence Guide | EvidentTrail"
      description="Complete guide to GitHub compliance for ISO 27001 A.8.32 change management. Branch protection configuration, continuous evidence, drift detection, and audit-ready packs for annual surveillance audits."
      h1="ISO 27001 GitHub Compliance Guide"
      heroText="GitHub is the technical backbone of your ISO 27001 A.8.32 change management procedure. This guide covers configuration, continuous evidence, and what surveillance auditors check across your full certificate period."
      type="article"
    >
      <BreadcrumbJsonLd
        items={[
          { name: 'Home', url: 'https://evidenttrail.com/' },
          { name: 'ISO 27001 GitHub Compliance', url: 'https://evidenttrail.com/iso-27001-github-compliance' },
        ]}
      />
      <FaqJsonLd items={faqs} />

      <section className={tokens.sectionAlt}>
        <div className={tokens.container}>
          <div className="max-w-3xl mx-auto">

            {/* A.8.32 Controls covered */}
            <h2 className={`${tokens.heading.h2} mb-6`}>Annex A Controls Covered by GitHub Evidence</h2>
            <div className="grid sm:grid-cols-2 gap-4 mb-12">
              {[
                {
                  control: 'A.8.32',
                  name: 'Change Management',
                  evidence: 'PR approvals, reviewer identity, CI check results, branch protection snapshots at time of change, exception records',
                },
                {
                  control: 'A.8.3 / A.8.5',
                  name: 'Access Restriction',
                  evidence: 'Branch protection push restrictions, 2FA enforcement records, access control lists, quarterly access review records',
                },
                {
                  control: 'A.8.9',
                  name: 'Configuration Management',
                  evidence: 'Branch protection drift events with before/after snapshots — unauthorized configuration changes captured with severity classification',
                },
                {
                  control: 'A.8.8',
                  name: 'Vulnerability Management',
                  evidence: 'Secret scanning alerts, security CI check results — monitoring evidence linked to change records',
                },
              ].map(({ control, name, evidence }) => (
                <div key={control} className={tokens.card}>
                  <div className="flex items-center gap-2 mb-2">
                    <span className="bg-indigo-900/50 text-indigo-300 px-2 py-0.5 rounded text-xs font-mono">{control}</span>
                    <h3 className="text-white font-semibold">{name}</h3>
                  </div>
                  <p className="text-sm text-slate-400">{evidence}</p>
                </div>
              ))}
            </div>

            {/* A.8.32 requirement mapping */}
            <h2 className={`${tokens.heading.h2} mb-6`}>A.8.32 Requirements Mapped to GitHub</h2>
            <div className="space-y-4 mb-12">
              {[
                {
                  requirement: 'Authorization',
                  standard: 'Changes must be formally authorized before implementation.',
                  evidence: 'Pull request creation records the change request. Branch protection rules enforce minimum reviewer counts before merge is permitted.',
                },
                {
                  requirement: 'Risk & Impact Assessment',
                  standard: 'The impact of the change must be considered before implementation.',
                  evidence: 'Code review activity recorded per reviewer. Required CI checks provide automated risk signals — test failures, security scans.',
                },
                {
                  requirement: 'Testing',
                  standard: 'Changes must be tested before deployment to production.',
                  evidence: 'Required CI status check results captured with pass/fail and timestamp — linked to the specific PR and reviewer.',
                },
                {
                  requirement: 'Approval',
                  standard: 'Changes require explicit approval from an authorized person.',
                  evidence: 'Reviewer identity, approval decision, and timestamp per PR. Author cannot self-approve — segregation of duty enforced by branch protection.',
                },
                {
                  requirement: 'Record-Keeping',
                  standard: 'A record of changes and their authorization must be maintained throughout the certificate period.',
                  evidence: 'Continuous capture from installation date. Immutable evidence packs with SHA-256 checksums for all sampled periods.',
                },
                {
                  requirement: 'Exception Handling',
                  standard: 'Emergency changes outside normal procedure must be documented with justification.',
                  evidence: 'Exception records capture emergency merges with lifecycle tracking, linked PRs, justification fields, and remediation documentation.',
                },
              ].map(({ requirement, standard, evidence }) => (
                <div key={requirement} className={tokens.card}>
                  <div className="flex items-center justify-between mb-3 border-b border-slate-700 pb-2">
                    <h3 className="text-white font-bold">{requirement}</h3>
                    <span className="bg-indigo-900/50 text-indigo-300 px-2 py-0.5 rounded text-xs font-mono">A.8.32</span>
                  </div>
                  <p className="text-slate-500 text-xs mb-2 italic">{standard}</p>
                  <div className="bg-slate-900/50 p-3 rounded-lg">
                    <span className="text-primary-400 text-xs font-medium">EvidentTrail evidence: </span>
                    <span className="text-slate-300 text-sm">{evidence}</span>
                  </div>
                </div>
              ))}
            </div>

            {/* Certificate period timeline */}
            <h2 className={`${tokens.heading.h2} mb-6`}>ISO 27001 Certificate Period & Evidence</h2>
            <div className="overflow-x-auto mb-12">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-700">
                    <th className="text-left py-3 px-4 text-slate-300 font-medium">Audit Event</th>
                    <th className="text-left py-3 px-4 text-slate-300 font-medium">Timing</th>
                    <th className="text-left py-3 px-4 text-slate-300 font-medium">Evidence Sampled</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-800">
                  {[
                    ['Initial Certification', 'Year 0', 'Full ISMS documentation + representative change evidence'],
                    ['Surveillance Audit 1', 'Year 1', 'Changes from months 1–12 of the certificate'],
                    ['Surveillance Audit 2', 'Year 2', 'Changes from months 13–24 of the certificate'],
                    ['Recertification Audit', 'Year 3', 'Full ISMS review + changes since last surveillance audit'],
                  ].map(([event, timing, evidence]) => (
                    <tr key={event} className="hover:bg-slate-800/30">
                      <td className="py-3 px-4 text-slate-300 font-medium">{event}</td>
                      <td className="py-3 px-4 text-slate-400">{timing}</td>
                      <td className="py-3 px-4 text-slate-400">{evidence}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="text-center">
              <a href={appLinks.register} className="btn btn-primary px-8 py-3">
                Start Collecting ISO 27001 Evidence
              </a>
            </div>
          </div>
        </div>
      </section>

      <SeoLongformSections sections={longformSections} faqs={faqs} />

      <section className={tokens.section}>
        <div className={tokens.container}>
          <h2 className={`${tokens.heading.h3} mb-8 text-center`}>Related ISO 27001 Resources</h2>
          <div className="grid md:grid-cols-4 gap-6">
            <Link to="/solutions/iso-27001-a832-evidence" className={`${tokens.card} hover:border-primary-500/50 transition-colors`}>
              <h4 className="text-white font-medium mb-2">ISO 27001 A.8.32 Deep Dive</h4>
              <p className="text-sm text-slate-400">Control-level mapping of GitHub evidence to all A.8.32 requirements.</p>
            </Link>
            <Link to="/iso-27001-vs-soc2" className={`${tokens.card} hover:border-primary-500/50 transition-colors`}>
              <h4 className="text-white font-medium mb-2">ISO 27001 vs SOC 2</h4>
              <p className="text-sm text-slate-400">60–70% evidence overlap — pursue both with a single EvidentTrail installation.</p>
            </Link>
            <Link to="/branch-protection-drift-detection" className={`${tokens.card} hover:border-primary-500/50 transition-colors`}>
              <h4 className="text-white font-medium mb-2">Branch Protection Drift</h4>
              <p className="text-sm text-slate-400">Detect and record unauthorized branch protection changes across the certificate period.</p>
            </Link>
            <Link to="/tools/branch-protection-snapshot" className={`${tokens.card} hover:border-primary-500/50 transition-colors`}>
              <h4 className="text-white font-medium mb-2">Branch Protection Snapshot</h4>
              <p className="text-sm text-slate-400">Free tool — export your current branch protection configuration as evidence.</p>
            </Link>
          </div>
        </div>
      </section>
    </MarketingLayout>
  );
}
